import { useQuery } from '@tanstack/react-query'
import { LeitstandAufraeumen } from '../components/leitstand/Aufraeumen'
import { DailyKnopf } from '../components/leitstand/Daily'
import { LeitstandHeute } from '../components/leitstand/Heute'
import { LeitstandRueckblick, ZEITRAEUME } from '../components/leitstand/Rueckblick'
import { Topbar } from '../components/Topbar'
import { Card, CardPad } from '../components/ui/primitives'
import { useMerker } from '../lib/merker'
import { altlastenQuery, leitstandHeuteQuery, sitzungQuery } from '../lib/queries'

const REITER = ['heute', 'rueckblick', 'aufraeumen'] as const
type Reiter = (typeof REITER)[number]

const UNTERTITEL: Record<Reiter, string> = {
  heute: 'Was heute Aufmerksamkeit braucht – und wer wie viel vor sich hat',
  rueckblick: 'Wie die Arbeit gelaufen ist – aus dem Ereignis-Log',
  aufraeumen: 'Was so lange liegt, dass es die anderen Zahlen verfälscht',
}

/**
 * Leitstand (Konzept Kap. 5.5) – **neu geordnet nach den Fragen der Leitung** (17.09.2026,
 * [E-358](../../../../docs/ENTSCHEIDUNGEN.md#e-358)).
 *
 * Nils: *„Der Leitstand ist irgendwie doch ganz schön unübersichtlich geworden."* Die alte
 * Seite war rund 4.200 px lang, zeigte dieselben Personen in sechs Blöcken mit je eigener
 * Auswahl und Zählweise, und die Frage *„wo muss ich heute eingreifen?"* stand nirgends – ihre
 * Antworten steckten in Tabellenzellen. Entschieden hat Nils am 17.09.2026 (per Klick):
 *
 * - **Drei Reiter** – Heute · Rückblick · Aufräumen. Der gewählte wird gemerkt.
 * - **Team-Tabelle** nur mit Personen, die Arbeit haben; der Rest eingeklappt.
 * - **Altlast** = 90 Tage ohne Bewegung – steht unter „Aufräumen", nicht in „Heute".
 * - **„Größen" und „möglich"** raus aus der Übersicht: Größen im Popup von „wartet",
 *   „kommt in Frage" im Zuweisen-Dialog.
 * - **Alle sieben Regeln** in „Eingreifen", **alle Trends** im Rückblick.
 *
 * Der Entwurf, an dem das abgenommen wurde, liegt als `mockup-leitstand-entwurf.html` im Repo.
 */
export function Leitstand() {
  const { data: sitzung } = useQuery(sitzungQuery)
  const [reiter, setReiter] = useMerker<Reiter>('leitstand-reiter', 'heute', REITER)
  const [zeitraum, setZeitraum] = useMerker('leitstand-zeitraum', '14', ZEITRAEUME)

  /**
   * Nur die Leitung (E-19, beantwortet F-03). Das Ausblenden hier ist die Höflichkeit – die
   * Durchsetzung steht im Backend an jeder Prozedur.
   */
  const darfSehen = sitzung?.role === 'steuerer' || sitzung?.role === 'admin'

  /**
   * „Heute" lädt immer: der Reiter trägt die Zahl der Punkte, und der Rückblick nimmt daraus
   * die Mitglieder der Arbeitswelt für die Aktivität.
   */
  const { data: heute } = useQuery({ ...leitstandHeuteQuery, enabled: darfSehen })
  const { data: altlasten } = useQuery({ ...altlastenQuery, enabled: darfSehen })

  if (!darfSehen) {
    return (
      <>
        <Topbar titel="Leitstand" untertitel="Auswertung der Team-Arbeit" />
        <Card>
          <CardPad>
            <div className="text-[13px] font-semibold">
              Diese Ansicht ist der Leitung vorbehalten.
            </div>
            <p className="mt-1.5 text-xs text-ink2">
              Die personenbezogene Auswertung sehen nur die Rollen „Steuerer" und „Admin"
              (Entscheidung E-19). Durchgesetzt wird das im Backend, nicht durch dieses Ausblenden.
            </p>
          </CardPad>
        </Card>
      </>
    )
  }

  const kritisch = heute?.lage.punkte.filter((p) => p.schwere === 'kritisch').length ?? 0
  const punkte = heute?.lage.punkte.length

  return (
    <>
      <Topbar
        titel="Leitstand"
        untertitel={UNTERTITEL[reiter]}
        aktion={
          /**
           * **Der Daily-Knopf steht neben dem, was der Reiter gerade anbietet**
           * ([Z-18](../../../docs/phasen/zusatz-18-daily.md)). Er gehört zu keinem der Reiter:
           * Die Morgenbesprechung ist der Einstieg in den Tag, nicht eine Auswertung darin.
           */
          <div className="flex items-center gap-2.5">
            <DailyKnopf />
            {reiter === 'rueckblick' ? (
              <div className="flex items-center gap-1.5" role="group" aria-label="Zeitraum">
                {ZEITRAEUME.map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => setZeitraum(z)}
                    aria-pressed={zeitraum === z}
                    className={`cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold ${
                      zeitraum === z
                        ? 'bg-ink text-white'
                        : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                    }`}
                  >
                    {z} Tage
                  </button>
                ))}
              </div>
            ) : reiter === 'heute' && heute ? (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted">
                <span aria-hidden className="h-[7px] w-[7px] rounded-full bg-[#0ca30c]" />
                Stand{' '}
                {new Date(heute.stand).toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ) : null}
          </div>
        }
      />

      <div
        role="tablist"
        aria-label="Bereiche des Leitstands"
        className="mb-4 inline-flex gap-0.5 rounded-[9px] bg-[#ecebe7] p-[3px]"
      >
        {REITER.map((r) => {
          const zahl = r === 'heute' ? punkte : r === 'aufraeumen' ? altlasten?.length : undefined
          return (
            <button
              key={r}
              type="button"
              role="tab"
              aria-selected={reiter === r}
              onClick={() => setReiter(r)}
              className={`flex cursor-pointer items-center gap-1.5 rounded-[7px] px-3.5 py-[5px] text-[12.5px] font-semibold ${
                reiter === r
                  ? 'bg-surface text-ink shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                  : 'text-ink2'
              }`}
            >
              {r === 'heute' ? 'Heute' : r === 'rueckblick' ? 'Rückblick' : 'Aufräumen'}
              {zahl !== undefined && zahl > 0 && (
                <span
                  className={`rounded-full px-1.5 text-[10.5px] leading-4 text-white ${
                    r === 'heute' && kritisch > 0 ? 'bg-pool-sofort' : 'bg-muted'
                  }`}
                >
                  {zahl}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div role="tabpanel">
        {reiter === 'heute' && <LeitstandHeute daten={heute} />}
        {reiter === 'rueckblick' && <LeitstandRueckblick zeitraum={zeitraum} heute={heute} />}
        {reiter === 'aufraeumen' && <LeitstandAufraeumen />}
      </div>
    </>
  )
}
